import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Calendar, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ContactForm } from "@/components/ContactForm";
import { CalendlyModal } from "@/components/CalendlyModal";
import logo from "@/assets/logo.jpg";

interface SharedHeaderProps {
  showNav?: boolean;
  onNavClick?: (sectionId: string) => void;
}

export function SharedHeader({ showNav = false, onNavClick }: SharedHeaderProps) {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (sectionId: string) => {
    if (onNavClick) {
      onNavClick(sectionId);
    }
    setMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-10 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background/95 backdrop-blur-md shadow-md" : "bg-background/95 backdrop-blur-md shadow-md"
      }`}
    >
      <div className="container mx-auto px-3 sm:px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0">
            <img src={logo} alt="Call24x7 Logo" className="h-7 w-7 sm:h-8 sm:w-8" width="32" height="32" />
            <span className="text-lg sm:text-xl md:text-2xl font-bold text-primary">Call24x7</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
            {showNav && (
              <>
                <button 
                  onClick={() => handleNavClick("features")} 
                  className="text-sm xl:text-base text-muted-foreground hover:text-primary transition-colors"
                >
                  {t("header.features")}
                </button>
                <button 
                  onClick={() => handleNavClick("api")} 
                  className="text-sm xl:text-base text-muted-foreground hover:text-primary transition-colors"
                >
                  {t("header.api")}
                </button>
                <button 
                  onClick={() => handleNavClick("pricing")} 
                  className="text-sm xl:text-base text-muted-foreground hover:text-primary transition-colors"
                >
                  {t("header.pricing")}
                </button>
              </>
            )}
            <Link to="/blog" className="text-sm xl:text-base text-muted-foreground hover:text-primary transition-colors">
              {t("header.blog")}
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="text-sm xl:text-base text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                {t("header.resources")} <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-background border border-border shadow-lg z-50">
                <DropdownMenuItem asChild>
                  <Link to="/industries" className="cursor-pointer">
                    {t("header.industries")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/case-studies" className="cursor-pointer">
                    {t("header.caseStudies")}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3">
            <LanguageSelector />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCalendlyOpen(true)}
              className="gap-1"
            >
              <Calendar className="h-4 w-4" />
              {t("header.bookMeeting")}
            </Button>
            <ContactForm />
            <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} />
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => window.location.href = "https://manage.call24x7.ai/login"}
            >
              {t("header.login")}
            </Button>
            <Button 
              size="sm" 
              onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}
            >
              {t("header.signUp")}
            </Button>
          </div>

          {/* Mobile Actions */}
          <div className="flex md:hidden items-center gap-1 sm:gap-2">
            <LanguageSelector />
            <Button 
              size="sm" 
              className="text-xs px-2 sm:px-3 h-8" 
              onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}
            >
              {t("header.signUp")}
            </Button>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-10 sm:w-10 shrink-0" aria-label="Open menu">
                  <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[300px] bg-background">
                <nav className="flex flex-col gap-4 mt-8">
                  {showNav && (
                    <>
                      <button 
                        onClick={() => handleNavClick("features")} 
                        className="text-left py-3 px-4 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                      >
                        {t("header.features")}
                      </button>
                      <button 
                        onClick={() => handleNavClick("api")} 
                        className="text-left py-3 px-4 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                      >
                        {t("header.api")}
                      </button>
                      <button 
                        onClick={() => handleNavClick("pricing")} 
                        className="text-left py-3 px-4 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                      >
                        {t("header.pricing")}
                      </button>
                    </>
                  )}
                  <Link 
                    to="/blog" 
                    onClick={() => setMobileMenuOpen(false)} 
                    className="text-left py-3 px-4 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    {t("header.blog")}
                  </Link>
                  <div className="px-4 py-2 text-sm font-semibold text-muted-foreground">{t("header.resources")}</div>
                  <Link 
                    to="/industries" 
                    onClick={() => setMobileMenuOpen(false)} 
                    className="text-left py-3 px-4 pl-8 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    {t("header.industries")}
                  </Link>
                  <Link 
                    to="/case-studies" 
                    onClick={() => setMobileMenuOpen(false)} 
                    className="text-left py-3 px-4 pl-8 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    {t("header.caseStudies")}
                  </Link>
                  <Link 
                    to="/" 
                    onClick={() => setMobileMenuOpen(false)} 
                    className="text-left py-3 px-4 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    {t("header.home")}
                  </Link>

                  <div className="pt-4 mt-4 border-t border-border flex flex-col gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setCalendlyOpen(true);
                        setMobileMenuOpen(false);
                      }} 
                      className="w-full h-12 text-base gap-2"
                    >
                      <Calendar className="h-5 w-5" />
                      {t("header.bookMeeting")}
                    </Button>
                    <div className="px-4">
                      <ContactForm />
                    </div>
                    <Button 
                      variant="ghost" 
                      onClick={() => window.location.href = "https://manage.call24x7.ai/login"} 
                      className="w-full justify-start h-12 text-base"
                    >
                      {t("header.login")}
                    </Button>
                    <Button 
                      onClick={() => window.location.href = "https://manage.call24x7.ai/signup"} 
                      className="w-full h-12 text-base"
                    >
                      {t("header.signUp")}
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
